import React, { useState, useEffect } from 'react';
import { Layout } from '../components/Layout';
import { proveedoresAPI, authAPI } from '../lib/api';
import { formatNumber, formatCurrency } from '../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Skeleton } from '../components/ui/skeleton';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import {
  Plus,
  Users,
  MagnifyingGlass,
  CurrencyDollar,
  Receipt,
  Trash,
  Eye,
  Money,
  CheckCircle,
  PencilSimple,
  Building,
  Phone,
  EnvelopeSimple,
  ShieldCheck,
  Warning,
} from '@phosphor-icons/react';

export default function Proveedores() {
  const [proveedores, setProveedores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busqueda, setBusqueda] = useState('');
  
  // Modal de nuevo proveedor
  const [showNewModal, setShowNewModal] = useState(false);
  const [newProveedor, setNewProveedor] = useState({
    nombre: '',
    rnc: '',
    moneda_habitual: 'DOP',
    telefono: '',
    email: '',
    direccion: '',
    notas: '',
  });
  const [savingNew, setSavingNew] = useState(false);
  
  // Modal de pago
  const [showPagoModal, setShowPagoModal] = useState(false);
  const [proveedorSeleccionado, setProveedorSeleccionado] = useState(null);
  const [montoPago, setMontoPago] = useState('');
  const [monedaPago, setMonedaPago] = useState('DOP');
  const [metodoPago, setMetodoPago] = useState('transferencia');
  const [referenciaPago, setReferenciaPago] = useState('');
  const [bancoPago, setBancoPago] = useState('');
  const [notaPago, setNotaPago] = useState('');
  const [imagenComprobante, setImagenComprobante] = useState(null);
  const [imagenPreview, setImagenPreview] = useState(null);
  const [loadingPago, setLoadingPago] = useState(false);
  
  // Modal de historial de pagos
  const [showHistorialPagos, setShowHistorialPagos] = useState(false);
  const [historialPagos, setHistorialPagos] = useState(null);
  const [loadingHistorial, setLoadingHistorial] = useState(false);
  
  // Modal ver comprobante
  const [showComprobanteModal, setShowComprobanteModal] = useState(false);
  const [comprobanteSeleccionado, setComprobanteSeleccionado] = useState(null);
  
  // Modal editar pago
  const [showEditarPagoModal, setShowEditarPagoModal] = useState(false);
  const [pagoSeleccionado, setPagoSeleccionado] = useState(null);
  const [editPagoData, setEditPagoData] = useState({});
  const [editImagenPreview, setEditImagenPreview] = useState(null);
  const [superUserCodeEdit, setSuperUserCodeEdit] = useState('');
  const [loadingEditPago, setLoadingEditPago] = useState(false);
  
  // Modal eliminar pago
  const [showEliminarPagoModal, setShowEliminarPagoModal] = useState(false);
  const [superUserCodeEliminar, setSuperUserCodeEliminar] = useState('');
  const [loadingEliminarPago, setLoadingEliminarPago] = useState(false);
  
  // Modal de detalle/facturas
  const [showDetalleModal, setShowDetalleModal] = useState(false);
  const [detalleData, setDetalleData] = useState(null);
  const [loadingDetalle, setLoadingDetalle] = useState(false);
  
  // Modal de edición con super usuario
  const [showEditModal, setShowEditModal] = useState(false);
  const [proveedorAEditar, setProveedorAEditar] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [savingEdit, setSavingEdit] = useState(false);
  const [superuserCode, setSuperuserCode] = useState('');
  const [validatingSuperuser, setValidatingSuperuser] = useState(false);
  const [superuserValidated, setSuperuserValidated] = useState(false);
  
  // Modal de eliminar con super usuario
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [proveedorAEliminar, setProveedorAEliminar] = useState(null);
  const [deleteCode, setDeleteCode] = useState('');
  const [validatingDelete, setValidatingDelete] = useState(false);
  const [deletingProveedor, setDeletingProveedor] = useState(false);

  useEffect(() => {
    loadProveedores();
  }, []);

  const loadProveedores = async () => {
    try {
      const response = await proveedoresAPI.getAll();
      setProveedores(response.data);
    } catch (error) {
      toast.error('Error al cargar proveedores');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateProveedor = async () => {
    if (!newProveedor.nombre.trim()) {
      toast.error('El nombre del proveedor es obligatorio');
      return;
    }
    
    setSavingNew(true);
    try {
      await proveedoresAPI.create(newProveedor);
      toast.success('Proveedor creado exitosamente');
      setShowNewModal(false);
      setNewProveedor({
        nombre: '',
        rnc: '',
        moneda_habitual: 'DOP',
        telefono: '',
        email: '',
        direccion: '',
        notas: '',
      });
      loadProveedores();
    } catch (error) {
      toast.error('Error al crear proveedor');
    } finally {
      setSavingNew(false);
    }
  };

  const abrirModalPago = (proveedor) => {
    setProveedorSeleccionado(proveedor);
    setMontoPago('');
    setMonedaPago(proveedor.moneda_habitual);
    setMetodoPago('transferencia');
    setReferenciaPago('');
    setBancoPago('');
    setNotaPago('');
    setImagenComprobante(null);
    setImagenPreview(null);
    setShowPagoModal(true);
  };

  const handleImagenChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error('La imagen no debe superar 5MB');
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagenComprobante(reader.result);
        setImagenPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handlePago = async () => {
    if (!montoPago || parseFloat(montoPago) <= 0) {
      toast.error('Ingrese un monto válido');
      return;
    }
    
    setLoadingPago(true);
    try {
      await proveedoresAPI.registrarPago(proveedorSeleccionado.id, {
        monto: parseFloat(montoPago),
        moneda: monedaPago,
        metodo_pago: metodoPago,
        referencia: referenciaPago || null,
        banco: bancoPago || null,
        nota: notaPago || null,
        imagen_comprobante: imagenComprobante,
      });
      toast.success('Pago registrado correctamente');
      setShowPagoModal(false);
      loadProveedores();
    } catch (error) {
      toast.error('Error al registrar pago');
    } finally {
      setLoadingPago(false);
    }
  };

  const verHistorialPagos = async (proveedor) => {
    setProveedorSeleccionado(proveedor);
    setLoadingHistorial(true);
    setShowHistorialPagos(true);
    
    try {
      const response = await proveedoresAPI.getPagos(proveedor.id);
      setHistorialPagos(response.data);
    } catch (error) {
      toast.error('Error al cargar historial de pagos');
    } finally {
      setLoadingHistorial(false);
    }
  };

  const verComprobante = (imagen) => {
    setComprobanteSeleccionado(imagen);
    setShowComprobanteModal(true);
  };

  const abrirEditarPago = (pago) => {
    setPagoSeleccionado(pago);
    setEditPagoData({
      monto: pago.monto?.toString() || '',
      moneda: pago.moneda || 'DOP',
      metodo_pago: pago.metodo_pago || 'transferencia',
      referencia: pago.referencia || '',
      banco: pago.banco || '',
      nota: pago.nota || '',
      fecha: pago.fecha ? pago.fecha.split('T')[0] : '',
      imagen_comprobante: pago.imagen_comprobante || null,
    });
    setEditImagenPreview(pago.imagen_comprobante || null);
    setSuperUserCodeEdit('');
    setShowEditarPagoModal(true);
  };

  const handleEditImagenChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error('La imagen no debe superar 5MB');
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        setEditPagoData(prev => ({ ...prev, imagen_comprobante: reader.result }));
        setEditImagenPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleGuardarEdicionPago = async () => {
    if (!editPagoData.monto || parseFloat(editPagoData.monto) <= 0) {
      toast.error('Ingrese un monto válido');
      return;
    }
    if (!superUserCodeEdit) {
      toast.error('Ingrese el código de super usuario');
      return;
    }
    
    setLoadingEditPago(true);
    try {
      await proveedoresAPI.editarPago(
        proveedorSeleccionado.id,
        pagoSeleccionado.id,
        {
          monto: parseFloat(editPagoData.monto),
          moneda: editPagoData.moneda,
          metodo_pago: editPagoData.metodo_pago,
          referencia: editPagoData.referencia || null,
          banco: editPagoData.banco || null,
          nota: editPagoData.nota || null,
          fecha: editPagoData.fecha || null,
          imagen_comprobante: editPagoData.imagen_comprobante,
        },
        superUserCodeEdit
      );
      toast.success('Pago actualizado correctamente');
      setShowEditarPagoModal(false);
      // Recargar historial
      verHistorialPagos(proveedorSeleccionado);
      loadProveedores();
    } catch (error) {
      const msg = error.response?.data?.detail || 'Error al actualizar pago';
      toast.error(msg);
    } finally {
      setLoadingEditPago(false);
    }
  };

  const abrirEliminarPago = (pago) => {
    setPagoSeleccionado(pago);
    setSuperUserCodeEliminar('');
    setShowEliminarPagoModal(true);
  };

  const handleEliminarPago = async () => {
    if (!superUserCodeEliminar) {
      toast.error('Ingrese el código de super usuario');
      return;
    }
    
    setLoadingEliminarPago(true);
    try {
      await proveedoresAPI.eliminarPago(
        proveedorSeleccionado.id,
        pagoSeleccionado.id,
        superUserCodeEliminar
      );
      toast.success('Pago eliminado correctamente');
      setShowEliminarPagoModal(false);
      // Recargar historial
      verHistorialPagos(proveedorSeleccionado);
      loadProveedores();
    } catch (error) {
      const msg = error.response?.data?.detail || 'Error al eliminar pago';
      toast.error(msg);
    } finally {
      setLoadingEliminarPago(false);
    }
  };

  const verDetalle = async (proveedor) => {
    setProveedorSeleccionado(proveedor);
    setLoadingDetalle(true);
    setShowDetalleModal(true);
    
    try {
      const response = await proveedoresAPI.getFacturas(proveedor.id);
      setDetalleData(response.data);
    } catch (error) {
      toast.error('Error al cargar detalle');
    } finally {
      setLoadingDetalle(false);
    }
  };

  // Funciones de edición con super usuario
  const abrirModalEditar = (proveedor) => {
    setProveedorAEditar(proveedor);
    setEditForm({
      nombre: proveedor.nombre,
      rnc: proveedor.rnc || '',
      moneda_habitual: proveedor.moneda_habitual,
      telefono: proveedor.telefono || '',
      email: proveedor.email || '',
      direccion: proveedor.direccion || '',
      notas: proveedor.notas || '',
    });
    setSuperuserCode('');
    setSuperuserValidated(false);
    setShowEditModal(true);
  };

  const validateSuperuserCode = async () => {
    if (!superuserCode) {
      toast.error('Ingrese el código de super usuario');
      return;
    }
    setValidatingSuperuser(true);
    try {
      await authAPI.validateSuperuser(superuserCode);
      setSuperuserValidated(true);
      toast.success('Código validado correctamente');
    } catch (error) {
      toast.error('Código de super usuario inválido');
    } finally {
      setValidatingSuperuser(false);
    }
  };

  const handleEditProveedor = async () => {
    if (!superuserValidated || !proveedorAEditar) return;
    
    setSavingEdit(true);
    try {
      await proveedoresAPI.update(proveedorAEditar.id, editForm, superuserCode);
      toast.success('Proveedor actualizado exitosamente');
      setShowEditModal(false);
      loadProveedores();
    } catch (error) {
      toast.error('Error al actualizar proveedor');
    } finally {
      setSavingEdit(false);
    }
  };

  // Funciones de eliminar con super usuario
  const abrirModalEliminar = (proveedor) => {
    setProveedorAEliminar(proveedor);
    setDeleteCode('');
    setShowDeleteModal(true);
  };

  const handleDeleteProveedor = async () => {
    if (!deleteCode || !proveedorAEliminar) {
      toast.error('Ingrese el código de super usuario');
      return;
    }
    
    setValidatingDelete(true);
    try {
      // Validar primero
      await authAPI.validateSuperuser(deleteCode);
      
      // Luego eliminar
      setDeletingProveedor(true);
      await proveedoresAPI.delete(proveedorAEliminar.id, deleteCode);
      toast.success('Proveedor eliminado exitosamente');
      setShowDeleteModal(false);
      loadProveedores();
    } catch (error) {
      if (error.response?.status === 401) {
        toast.error('Código de super usuario inválido');
      } else {
        toast.error(error.response?.data?.detail || 'Error al eliminar proveedor');
      }
    } finally {
      setValidatingDelete(false);
      setDeletingProveedor(false);
    }
  };

  const proveedoresFiltrados = proveedores.filter(p =>
    p.nombre.toLowerCase().includes(busqueda.toLowerCase()) ||
    (p.rnc && p.rnc.includes(busqueda))
  );

  const totalPendiente = proveedores.reduce((sum, p) => sum + (p.balance_pendiente_dop || 0), 0);
  const proveedoresConDeuda = proveedores.filter(p => p.balance_pendiente > 0).length;

  if (loading) {
    return (
      <Layout>
        <div className="space-y-4">
          <Skeleton className="h-32 w-full" />
          <Skeleton className="h-64 w-full" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
              <Building size={28} className="text-[#F69031]" />
              Proveedores
            </h1>
            <p className="text-muted-foreground">Gestión de proveedores y cuentas por pagar</p>
          </div>
          <Button 
            onClick={() => setShowNewModal(true)}
            className="bg-[#E73425] hover:bg-[#D81C23]"
            data-testid="nuevo-proveedor-btn"
          >
            <Plus size={18} className="mr-2" />
            Nuevo Proveedor
          </Button>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className="border-l-4 border-l-[#F69031]">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Total Proveedores</p>
                  <p className="text-2xl font-bold">{proveedores.length}</p>
                </div>
                <Users size={32} className="text-[#F69031]" />
              </div>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-amber-500">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Con Deuda Pendiente</p>
                  <p className="text-2xl font-bold">{proveedoresConDeuda}</p>
                </div>
                <Receipt size={32} className="text-amber-500" />
              </div>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-red-500">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Total Pendiente (DOP)</p>
                  <p className="text-2xl font-bold text-red-600">{formatCurrency(totalPendiente)}</p>
                </div>
                <CurrencyDollar size={32} className="text-red-500" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Search */}
        <div className="relative max-w-md">
          <MagnifyingGlass className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground" size={18} />
          <Input
            placeholder="Buscar por nombre o RNC..."
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
            className="pl-10"
          />
        </div>

        {/* Proveedores List */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Lista de Proveedores</CardTitle>
          </CardHeader>
          <CardContent>
            {proveedoresFiltrados.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <Building size={48} className="mx-auto mb-2 opacity-30" />
                <p>No hay proveedores registrados</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b bg-muted/50">
                      <th className="p-3 text-left font-semibold">Proveedor</th>
                      <th className="p-3 text-left font-semibold">RNC</th>
                      <th className="p-3 text-center font-semibold">Moneda</th>
                      <th className="p-3 text-right font-semibold">Balance Pendiente</th>
                      <th className="p-3 text-center font-semibold">Facturas</th>
                      <th className="p-3 text-center font-semibold">Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {proveedoresFiltrados.map((proveedor) => (
                      <tr key={proveedor.id} className="border-b hover:bg-muted/30">
                        <td className="p-3">
                          <div>
                            <p className="font-medium">{proveedor.nombre}</p>
                            {proveedor.telefono && (
                              <p className="text-xs text-muted-foreground flex items-center gap-1">
                                <Phone size={12} /> {proveedor.telefono}
                              </p>
                            )}
                          </div>
                        </td>
                        <td className="p-3 text-muted-foreground">
                          {proveedor.rnc || '-'}
                        </td>
                        <td className="p-3 text-center">
                          <Badge variant={proveedor.moneda_habitual === 'USD' ? 'default' : 'secondary'}>
                            {proveedor.moneda_habitual}
                          </Badge>
                        </td>
                        <td className="p-3 text-right">
                          {proveedor.balance_pendiente > 0 ? (
                            <span className="font-semibold text-red-600">
                              {proveedor.moneda_habitual === 'USD' ? '$' : 'RD$'} {formatNumber(proveedor.balance_pendiente)}
                            </span>
                          ) : (
                            <span className="text-emerald-600">Sin deuda</span>
                          )}
                        </td>
                        <td className="p-3 text-center">
                          {proveedor.facturas_pendientes > 0 ? (
                            <Badge className="bg-amber-100 text-amber-700">
                              {proveedor.facturas_pendientes} pendientes
                            </Badge>
                          ) : (
                            <span className="text-muted-foreground">0</span>
                          )}
                        </td>
                        <td className="p-3">
                          <div className="flex items-center justify-center gap-1">
                            {proveedor.balance_pendiente > 0 && (
                              <Button
                                size="sm"
                                variant="outline"
                                className="h-8 px-2 text-emerald-600 hover:text-emerald-700"
                                onClick={() => abrirModalPago(proveedor)}
                                data-testid={`pagar-${proveedor.id}`}
                              >
                                <Money size={16} className="mr-1" />
                                Pagar
                              </Button>
                            )}
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-8 w-8 p-0 text-emerald-600 hover:text-emerald-700"
                              onClick={() => verHistorialPagos(proveedor)}
                              title="Ver historial de pagos"
                            >
                              <Receipt size={16} />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-8 w-8 p-0"
                              onClick={() => verDetalle(proveedor)}
                              title="Ver detalle"
                            >
                              <Eye size={16} />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-8 w-8 p-0 text-blue-600 hover:text-blue-700"
                              onClick={() => abrirModalEditar(proveedor)}
                              title="Editar proveedor"
                              data-testid={`editar-proveedor-${proveedor.id}`}
                            >
                              <PencilSimple size={16} />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-8 w-8 p-0 text-red-600 hover:text-red-700"
                              onClick={() => abrirModalEliminar(proveedor)}
                              title="Eliminar proveedor"
                              data-testid={`eliminar-proveedor-${proveedor.id}`}
                            >
                              <Trash size={16} />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Modal Nuevo Proveedor */}
        <Dialog open={showNewModal} onOpenChange={setShowNewModal}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Building size={24} className="text-[#F69031]" />
                Nuevo Proveedor
              </DialogTitle>
              <DialogDescription>
                Complete los datos del nuevo proveedor
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="nombre">Nombre del Proveedor *</Label>
                <Input
                  id="nombre"
                  value={newProveedor.nombre}
                  onChange={(e) => setNewProveedor(prev => ({ ...prev, nombre: e.target.value }))}
                  placeholder="Ej: Suministros Avícolas Pérez"
                  data-testid="proveedor-nombre"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="rnc">RNC (Opcional)</Label>
                  <Input
                    id="rnc"
                    value={newProveedor.rnc}
                    onChange={(e) => setNewProveedor(prev => ({ ...prev, rnc: e.target.value }))}
                    placeholder="000-00000-0"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="moneda">Moneda Habitual</Label>
                  <Select
                    value={newProveedor.moneda_habitual}
                    onValueChange={(value) => setNewProveedor(prev => ({ ...prev, moneda_habitual: value }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="DOP">DOP (Peso Dominicano)</SelectItem>
                      <SelectItem value="USD">USD (Dólar)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="telefono">Teléfono</Label>
                  <Input
                    id="telefono"
                    value={newProveedor.telefono}
                    onChange={(e) => setNewProveedor(prev => ({ ...prev, telefono: e.target.value }))}
                    placeholder="809-000-0000"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    value={newProveedor.email}
                    onChange={(e) => setNewProveedor(prev => ({ ...prev, email: e.target.value }))}
                    placeholder="proveedor@email.com"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="direccion">Dirección</Label>
                <Input
                  id="direccion"
                  value={newProveedor.direccion}
                  onChange={(e) => setNewProveedor(prev => ({ ...prev, direccion: e.target.value }))}
                  placeholder="Dirección del proveedor"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="notas">Notas</Label>
                <Textarea
                  id="notas"
                  value={newProveedor.notas}
                  onChange={(e) => setNewProveedor(prev => ({ ...prev, notas: e.target.value }))}
                  placeholder="Notas adicionales..."
                  rows={2}
                />
              </div>
            </div>
            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={() => setShowNewModal(false)}>
                Cancelar
              </Button>
              <Button
                className="bg-[#E73425] hover:bg-[#D81C23]"
                onClick={handleCreateProveedor}
                disabled={savingNew}
              >
                {savingNew ? (
                  <div className="spinner h-4 w-4" />
                ) : (
                  <>
                    <CheckCircle size={16} className="mr-2" />
                    Crear Proveedor
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Modal de Pago */}
        <Dialog open={showPagoModal} onOpenChange={setShowPagoModal}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Money size={24} className="text-emerald-600" />
                Registrar Pago a Proveedor
              </DialogTitle>
              <DialogDescription>
                Registre un pago o abono al proveedor
              </DialogDescription>
            </DialogHeader>
            {proveedorSeleccionado && (
              <div className="space-y-4">
                <div className="bg-muted/50 rounded-lg p-4 space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Proveedor:</span>
                    <span className="font-medium">{proveedorSeleccionado.nombre}</span>
                  </div>
                  <div className="flex justify-between border-t pt-2">
                    <span className="font-semibold">Deuda Pendiente:</span>
                    <span className="font-bold text-red-600 text-lg">
                      {proveedorSeleccionado.moneda_habitual === 'USD' ? '$' : 'RD$'} {formatNumber(proveedorSeleccionado.balance_pendiente)}
                    </span>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="monto-pago">Monto *</Label>
                    <Input
                      id="monto-pago"
                      type="number"
                      step="0.01"
                      value={montoPago}
                      onChange={(e) => setMontoPago(e.target.value)}
                      placeholder="0.00"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="moneda-pago">Moneda</Label>
                    <Select value={monedaPago} onValueChange={setMonedaPago}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="DOP">DOP</SelectItem>
                        <SelectItem value="USD">USD</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setMontoPago(proveedorSeleccionado.balance_pendiente.toString())}
                  >
                    Pago Total
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setMontoPago((proveedorSeleccionado.balance_pendiente / 2).toFixed(2))}
                  >
                    50%
                  </Button>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="metodo-pago">Método de Pago</Label>
                    <Select value={metodoPago} onValueChange={setMetodoPago}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="transferencia">Transferencia</SelectItem>
                        <SelectItem value="efectivo">Efectivo</SelectItem>
                        <SelectItem value="cheque">Cheque</SelectItem>
                        <SelectItem value="tarjeta">Tarjeta</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="banco-pago">Banco</Label>
                    <Input
                      id="banco-pago"
                      value={bancoPago}
                      onChange={(e) => setBancoPago(e.target.value)}
                      placeholder="Ej: Banreservas"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="referencia-pago">No. Referencia / Transacción</Label>
                  <Input
                    id="referencia-pago"
                    value={referenciaPago}
                    onChange={(e) => setReferenciaPago(e.target.value)}
                    placeholder="Ej: 123456789"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="nota-pago">Nota (opcional)</Label>
                  <Textarea
                    id="nota-pago"
                    value={notaPago}
                    onChange={(e) => setNotaPago(e.target.value)}
                    placeholder="Notas adicionales..."
                    rows={2}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="imagen-comprobante">Imagen del Comprobante</Label>
                  <Input
                    id="imagen-comprobante"
                    type="file"
                    accept="image/*"
                    onChange={handleImagenChange}
                    className="cursor-pointer"
                  />
                  {imagenPreview && (
                    <div className="mt-2 relative">
                      <img 
                        src={imagenPreview} 
                        alt="Comprobante" 
                        className="max-h-32 rounded border object-contain"
                      />
                      <Button
                        type="button"
                        variant="destructive"
                        size="sm"
                        className="absolute top-1 right-1"
                        onClick={() => {
                          setImagenComprobante(null);
                          setImagenPreview(null);
                        }}
                      >
                        <Trash size={14} />
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            )}
            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={() => setShowPagoModal(false)}>
                Cancelar
              </Button>
              <Button
                className="bg-emerald-600 hover:bg-emerald-700"
                onClick={handlePago}
                disabled={loadingPago || !montoPago}
              >
                {loadingPago ? (
                  <div className="spinner h-4 w-4" />
                ) : (
                  <>
                    <CheckCircle size={16} className="mr-2" />
                    Registrar Pago
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Modal de Detalle */}
        <Dialog open={showDetalleModal} onOpenChange={setShowDetalleModal}>
          <DialogContent className="sm:max-w-2xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Eye size={24} className="text-[#F69031]" />
                Detalle de Proveedor
              </DialogTitle>
            </DialogHeader>
            {proveedorSeleccionado && (
              <div className="space-y-4">
                <div className="bg-muted/50 rounded-lg p-4">
                  <h3 className="font-semibold text-lg">{proveedorSeleccionado.nombre}</h3>
                  {proveedorSeleccionado.rnc && <p className="text-sm text-muted-foreground">RNC: {proveedorSeleccionado.rnc}</p>}
                  <div className="mt-2 flex gap-4">
                    <Badge>{proveedorSeleccionado.moneda_habitual}</Badge>
                    <span className={`font-semibold ${proveedorSeleccionado.balance_pendiente > 0 ? 'text-red-600' : 'text-emerald-600'}`}>
                      Deuda: {proveedorSeleccionado.moneda_habitual === 'USD' ? '$' : 'RD$'} {formatNumber(proveedorSeleccionado.balance_pendiente)}
                    </span>
                  </div>
                </div>

                {loadingDetalle ? (
                  <div className="py-8 text-center">
                    <div className="spinner h-8 w-8 mx-auto" />
                  </div>
                ) : detalleData && (
                  <>
                    {/* Facturas */}
                    <div>
                      <h4 className="font-semibold mb-2">Facturas/Gastos ({detalleData.facturas?.length || 0})</h4>
                      {detalleData.facturas?.length > 0 ? (
                        <div className="space-y-2 max-h-48 overflow-y-auto">
                          {detalleData.facturas.map((f) => (
                            <div key={f.id} className="flex justify-between items-center bg-muted/30 rounded p-2 text-sm">
                              <div>
                                <p className="font-medium">{f.factura_numero || f.descripcion}</p>
                                <p className="text-xs text-muted-foreground">
                                  {new Date(f.fecha).toLocaleDateString('es-DO')} - {f.categoria}
                                </p>
                              </div>
                              <div className="text-right">
                                <p className={`font-semibold ${f.estado_pago === 'credito' ? 'text-amber-600' : 'text-emerald-600'}`}>
                                  {f.moneda === 'USD' ? '$' : 'RD$'} {formatNumber(f.total)}
                                </p>
                                <Badge variant={f.estado_pago === 'credito' ? 'secondary' : 'default'} className="text-xs">
                                  {f.estado_pago === 'credito' ? 'A Crédito' : 'Pagado'}
                                </Badge>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-muted-foreground text-sm">No hay facturas registradas</p>
                      )}
                    </div>

                    {/* Historial de Pagos */}
                    <div>
                      <h4 className="font-semibold mb-2">Historial de Pagos ({detalleData.pagos?.length || 0})</h4>
                      {detalleData.pagos?.length > 0 ? (
                        <div className="space-y-2 max-h-48 overflow-y-auto">
                          {detalleData.pagos.map((p, idx) => (
                            <div key={p.id || idx} className="flex justify-between items-center bg-emerald-50 rounded p-2 text-sm">
                              <div>
                                <p className="text-xs text-muted-foreground">
                                  {new Date(p.fecha).toLocaleDateString('es-DO')}
                                </p>
                                {p.nota && <p className="text-xs">{p.nota}</p>}
                              </div>
                              <p className="font-semibold text-emerald-600">
                                {p.moneda === 'USD' ? '$' : 'RD$'} {formatNumber(p.monto)}
                              </p>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-muted-foreground text-sm">No hay pagos registrados</p>
                      )}
                    </div>
                  </>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Modal de Editar Proveedor (con Super Usuario) */}
        <Dialog open={showEditModal} onOpenChange={(open) => {
          setShowEditModal(open);
          if (!open) {
            setSuperuserCode('');
            setSuperuserValidated(false);
          }
        }}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <PencilSimple size={24} className="text-blue-600" />
                Editar Proveedor
              </DialogTitle>
              <DialogDescription>
                {!superuserValidated 
                  ? 'Ingrese el código de super usuario para editar'
                  : 'Modifique los datos del proveedor'}
              </DialogDescription>
            </DialogHeader>
            
            {!superuserValidated ? (
              <div className="space-y-4">
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                  <div className="flex items-center gap-2 text-amber-700 mb-2">
                    <ShieldCheck size={20} />
                    <span className="font-medium">Acción Protegida</span>
                  </div>
                  <p className="text-sm text-amber-600">
                    La edición de proveedores requiere autorización de super usuario.
                  </p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-superuser-code">Código de Super Usuario</Label>
                  <Input
                    id="edit-superuser-code"
                    type="password"
                    value={superuserCode}
                    onChange={(e) => setSuperuserCode(e.target.value)}
                    placeholder="Ingrese el código..."
                    data-testid="edit-superuser-code"
                  />
                </div>
                <Button
                  onClick={validateSuperuserCode}
                  disabled={validatingSuperuser || !superuserCode}
                  className="w-full bg-[#E73425] hover:bg-[#D81C23]"
                >
                  {validatingSuperuser ? (
                    <div className="spinner h-4 w-4" />
                  ) : (
                    <>
                      <ShieldCheck size={16} className="mr-2" />
                      Validar Código
                    </>
                  )}
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="edit-nombre">Nombre del Proveedor *</Label>
                  <Input
                    id="edit-nombre"
                    value={editForm.nombre}
                    onChange={(e) => setEditForm(prev => ({ ...prev, nombre: e.target.value }))}
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="edit-rnc">RNC</Label>
                    <Input
                      id="edit-rnc"
                      value={editForm.rnc}
                      onChange={(e) => setEditForm(prev => ({ ...prev, rnc: e.target.value }))}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="edit-moneda">Moneda Habitual</Label>
                    <Select
                      value={editForm.moneda_habitual}
                      onValueChange={(value) => setEditForm(prev => ({ ...prev, moneda_habitual: value }))}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="DOP">DOP</SelectItem>
                        <SelectItem value="USD">USD</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="edit-telefono">Teléfono</Label>
                    <Input
                      id="edit-telefono"
                      value={editForm.telefono}
                      onChange={(e) => setEditForm(prev => ({ ...prev, telefono: e.target.value }))}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="edit-email">Email</Label>
                    <Input
                      id="edit-email"
                      type="email"
                      value={editForm.email}
                      onChange={(e) => setEditForm(prev => ({ ...prev, email: e.target.value }))}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="edit-direccion">Dirección</Label>
                  <Input
                    id="edit-direccion"
                    value={editForm.direccion}
                    onChange={(e) => setEditForm(prev => ({ ...prev, direccion: e.target.value }))}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="edit-notas">Notas</Label>
                  <Textarea
                    id="edit-notas"
                    value={editForm.notas}
                    onChange={(e) => setEditForm(prev => ({ ...prev, notas: e.target.value }))}
                    rows={2}
                  />
                </div>
              </div>
            )}
            
            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={() => setShowEditModal(false)}>
                Cancelar
              </Button>
              {superuserValidated && (
                <Button
                  className="bg-blue-600 hover:bg-blue-700"
                  onClick={handleEditProveedor}
                  disabled={savingEdit}
                >
                  {savingEdit ? (
                    <div className="spinner h-4 w-4" />
                  ) : (
                    <>
                      <CheckCircle size={16} className="mr-2" />
                      Guardar Cambios
                    </>
                  )}
                </Button>
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Modal de Eliminar Proveedor (con Super Usuario) */}
        <Dialog open={showDeleteModal} onOpenChange={setShowDeleteModal}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-red-600">
                <Warning size={24} />
                Eliminar Proveedor
              </DialogTitle>
              <DialogDescription>
                Esta acción no se puede deshacer
              </DialogDescription>
            </DialogHeader>
            
            {proveedorAEliminar && (
              <div className="space-y-4">
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <p className="text-sm text-red-600 mb-2">
                    Está a punto de eliminar al proveedor:
                  </p>
                  <p className="font-semibold text-red-700">{proveedorAEliminar.nombre}</p>
                  {proveedorAEliminar.balance_pendiente > 0 && (
                    <p className="text-sm text-red-600 mt-2">
                      ⚠️ Este proveedor tiene una deuda pendiente de{' '}
                      {proveedorAEliminar.moneda_habitual === 'USD' ? '$' : 'RD$'}{' '}
                      {formatNumber(proveedorAEliminar.balance_pendiente)}
                    </p>
                  )}
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="delete-superuser-code">Código de Super Usuario</Label>
                  <Input
                    id="delete-superuser-code"
                    type="password"
                    value={deleteCode}
                    onChange={(e) => setDeleteCode(e.target.value)}
                    placeholder="Ingrese el código para confirmar..."
                    data-testid="delete-superuser-code"
                  />
                </div>
              </div>
            )}
            
            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={() => setShowDeleteModal(false)}>
                Cancelar
              </Button>
              <Button
                variant="destructive"
                onClick={handleDeleteProveedor}
                disabled={validatingDelete || deletingProveedor || !deleteCode}
              >
                {validatingDelete || deletingProveedor ? (
                  <div className="spinner h-4 w-4" />
                ) : (
                  <>
                    <Trash size={16} className="mr-2" />
                    Eliminar Proveedor
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Modal de Historial de Pagos */}
        <Dialog open={showHistorialPagos} onOpenChange={setShowHistorialPagos}>
          <DialogContent className="sm:max-w-2xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Receipt size={24} className="text-emerald-600" />
                Historial de Pagos
              </DialogTitle>
              <DialogDescription>
                {proveedorSeleccionado?.nombre}
              </DialogDescription>
            </DialogHeader>
            
            {loadingHistorial ? (
              <div className="py-8 text-center">
                <div className="spinner h-8 w-8 mx-auto" />
              </div>
            ) : historialPagos && (
              <div className="space-y-4">
                {/* Resumen */}
                <div className="grid grid-cols-3 gap-4">
                  <div className="bg-emerald-50 rounded-lg p-3 text-center">
                    <p className="text-sm text-muted-foreground">Total Pagado DOP</p>
                    <p className="text-lg font-bold text-emerald-600">
                      RD$ {formatNumber(historialPagos.total_pagado_dop)}
                    </p>
                  </div>
                  <div className="bg-blue-50 rounded-lg p-3 text-center">
                    <p className="text-sm text-muted-foreground">Total Pagado USD</p>
                    <p className="text-lg font-bold text-blue-600">
                      $ {formatNumber(historialPagos.total_pagado_usd)}
                    </p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-3 text-center">
                    <p className="text-sm text-muted-foreground">Cantidad</p>
                    <p className="text-lg font-bold">{historialPagos.cantidad_pagos} pagos</p>
                  </div>
                </div>

                {/* Lista de pagos */}
                {historialPagos.pagos?.length > 0 ? (
                  <div className="space-y-3">
                    {historialPagos.pagos.map((pago) => (
                      <div key={pago.id} className="bg-muted/30 rounded-lg p-4 space-y-2">
                        <div className="flex justify-between items-start">
                          <div>
                            <p className="font-bold text-lg">
                              {pago.moneda === 'USD' ? '$' : 'RD$'} {formatNumber(pago.monto)}
                            </p>
                            <p className="text-sm text-muted-foreground">
                              {new Date(pago.fecha).toLocaleDateString('es-ES', {
                                year: 'numeric',
                                month: 'long',
                                day: 'numeric'
                              })}
                            </p>
                          </div>
                          <Badge variant="outline" className="capitalize">
                            {pago.metodo_pago || 'N/A'}
                          </Badge>
                        </div>
                        
                        <div className="grid grid-cols-2 gap-2 text-sm">
                          {pago.banco && (
                            <div>
                              <span className="text-muted-foreground">Banco: </span>
                              <span>{pago.banco}</span>
                            </div>
                          )}
                          {pago.referencia && (
                            <div>
                              <span className="text-muted-foreground">Ref: </span>
                              <span className="font-mono">{pago.referencia}</span>
                            </div>
                          )}
                        </div>
                        
                        {pago.nota && (
                          <p className="text-sm text-muted-foreground italic">
                            "{pago.nota}"
                          </p>
                        )}
                        
                        {pago.imagen_comprobante && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => verComprobante(pago.imagen_comprobante)}
                            className="mt-2"
                          >
                            <Eye size={14} className="mr-1" />
                            Ver Comprobante
                          </Button>
                        )}
                        
                        <div className="flex justify-between items-center mt-2 pt-2 border-t">
                          <p className="text-xs text-muted-foreground">
                            Registrado por: {pago.registrado_por_nombre || 'N/A'}
                            {pago.editado_at && (
                              <span className="ml-2 text-amber-600">
                                (Editado por {pago.editado_por_nombre})
                              </span>
                            )}
                          </p>
                          <div className="flex gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-7 text-blue-600 hover:text-blue-700"
                              onClick={() => abrirEditarPago(pago)}
                            >
                              <PencilSimple size={14} className="mr-1" />
                              Editar
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-7 text-red-600 hover:text-red-700"
                              onClick={() => abrirEliminarPago(pago)}
                            >
                              <Trash size={14} className="mr-1" />
                              Eliminar
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="py-8 text-center text-muted-foreground">
                    No hay pagos registrados para este proveedor
                  </div>
                )}
              </div>
            )}
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowHistorialPagos(false)}>
                Cerrar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Modal de Editar Pago */}
        <Dialog open={showEditarPagoModal} onOpenChange={setShowEditarPagoModal}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <PencilSimple size={24} className="text-blue-600" />
                Editar Pago
              </DialogTitle>
              <DialogDescription>
                Modifique los datos del pago
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="edit-monto">Monto *</Label>
                  <Input
                    id="edit-monto"
                    type="number"
                    step="0.01"
                    value={editPagoData.monto || ''}
                    onChange={(e) => setEditPagoData(prev => ({ ...prev, monto: e.target.value }))}
                    placeholder="0.00"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-moneda">Moneda</Label>
                  <Select 
                    value={editPagoData.moneda || 'DOP'} 
                    onValueChange={(v) => setEditPagoData(prev => ({ ...prev, moneda: v }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="DOP">DOP</SelectItem>
                      <SelectItem value="USD">USD</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="edit-metodo">Método de Pago</Label>
                  <Select 
                    value={editPagoData.metodo_pago || 'transferencia'} 
                    onValueChange={(v) => setEditPagoData(prev => ({ ...prev, metodo_pago: v }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="transferencia">Transferencia</SelectItem>
                      <SelectItem value="efectivo">Efectivo</SelectItem>
                      <SelectItem value="cheque">Cheque</SelectItem>
                      <SelectItem value="tarjeta">Tarjeta</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-banco">Banco</Label>
                  <Input
                    id="edit-banco"
                    value={editPagoData.banco || ''}
                    onChange={(e) => setEditPagoData(prev => ({ ...prev, banco: e.target.value }))}
                    placeholder="Ej: Banreservas"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="edit-referencia">No. Referencia / Transacción</Label>
                <Input
                  id="edit-referencia"
                  value={editPagoData.referencia || ''}
                  onChange={(e) => setEditPagoData(prev => ({ ...prev, referencia: e.target.value }))}
                  placeholder="Ej: 123456789"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="edit-nota">Nota</Label>
                <Textarea
                  id="edit-nota"
                  value={editPagoData.nota || ''}
                  onChange={(e) => setEditPagoData(prev => ({ ...prev, nota: e.target.value }))}
                  placeholder="Notas adicionales..."
                  rows={2}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="edit-imagen">Imagen del Comprobante</Label>
                <Input
                  id="edit-imagen"
                  type="file"
                  accept="image/*"
                  onChange={handleEditImagenChange}
                  className="cursor-pointer"
                />
                {editImagenPreview && (
                  <div className="mt-2 relative">
                    <img 
                      src={editImagenPreview} 
                      alt="Comprobante" 
                      className="max-h-32 rounded border object-contain"
                    />
                    <Button
                      type="button"
                      variant="destructive"
                      size="sm"
                      className="absolute top-1 right-1"
                      onClick={() => {
                        setEditPagoData(prev => ({ ...prev, imagen_comprobante: null }));
                        setEditImagenPreview(null);
                      }}
                    >
                      <Trash size={14} />
                    </Button>
                  </div>
                )}
              </div>

              <div className="space-y-2 pt-4 border-t">
                <Label htmlFor="edit-superuser-code" className="flex items-center gap-2 text-amber-600">
                  <ShieldCheck size={16} />
                  Código de Super Usuario *
                </Label>
                <Input
                  id="edit-superuser-code"
                  type="password"
                  value={superUserCodeEdit}
                  onChange={(e) => setSuperUserCodeEdit(e.target.value)}
                  placeholder="Ingrese el código"
                />
              </div>
            </div>
            
            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={() => setShowEditarPagoModal(false)}>
                Cancelar
              </Button>
              <Button
                className="bg-blue-600 hover:bg-blue-700"
                onClick={handleGuardarEdicionPago}
                disabled={loadingEditPago}
              >
                {loadingEditPago ? (
                  <div className="spinner h-4 w-4" />
                ) : (
                  <>
                    <CheckCircle size={16} className="mr-2" />
                    Guardar Cambios
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Modal de Eliminar Pago */}
        <Dialog open={showEliminarPagoModal} onOpenChange={setShowEliminarPagoModal}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-red-600">
                <Warning size={24} />
                Eliminar Pago
              </DialogTitle>
              <DialogDescription>
                Esta acción no se puede deshacer
              </DialogDescription>
            </DialogHeader>
            
            {pagoSeleccionado && (
              <div className="space-y-4">
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <p className="text-sm text-red-800">
                    ¿Está seguro que desea eliminar el pago de{' '}
                    <strong>
                      {pagoSeleccionado.moneda === 'USD' ? '$' : 'RD$'}{' '}
                      {formatNumber(pagoSeleccionado.monto)}
                    </strong>
                    ?
                  </p>
                  {pagoSeleccionado.referencia && (
                    <p className="text-xs text-red-600 mt-1">
                      Ref: {pagoSeleccionado.referencia}
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="eliminar-superuser-code" className="flex items-center gap-2 text-red-600">
                    <ShieldCheck size={16} />
                    Código de Super Usuario *
                  </Label>
                  <Input
                    id="eliminar-superuser-code"
                    type="password"
                    value={superUserCodeEliminar}
                    onChange={(e) => setSuperUserCodeEliminar(e.target.value)}
                    placeholder="Ingrese el código para confirmar"
                  />
                </div>
              </div>
            )}
            
            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={() => setShowEliminarPagoModal(false)}>
                Cancelar
              </Button>
              <Button
                variant="destructive"
                onClick={handleEliminarPago}
                disabled={loadingEliminarPago}
              >
                {loadingEliminarPago ? (
                  <div className="spinner h-4 w-4" />
                ) : (
                  <>
                    <Trash size={16} className="mr-2" />
                    Eliminar Pago
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Modal de Ver Comprobante */}
        <Dialog open={showComprobanteModal} onOpenChange={setShowComprobanteModal}>
          <DialogContent className="sm:max-w-xl">
            <DialogHeader>
              <DialogTitle>Comprobante de Pago</DialogTitle>
            </DialogHeader>
            {comprobanteSeleccionado && (
              <div className="flex justify-center">
                <img 
                  src={comprobanteSeleccionado} 
                  alt="Comprobante" 
                  className="max-h-[60vh] rounded border object-contain"
                />
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowComprobanteModal(false)}>
                Cerrar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
